import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/common/page-header";
import { PermissionGate } from "@/components/common/PermissionGate";
import { usePermissions } from "@/hooks/usePermissions";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { adminEmailTemplateService } from "@/services/admin-email-template.service";
import {
  EMAIL_TEMPLATE_CATEGORIES,
  type EmailTemplateListDto,
} from "@/types/email-template";

const CATEGORY_LABELS: Record<string, string> = {
  Auth: "Authentication",
  Payment: "Payment",
  Resource: "Resource",
  Security: "Security",
  System: "System",
};

export function EmailTemplatesListPage() {
  // Templates are readable by SuperAdmin and SupportAdmin; writing one is SuperAdmin-only.
  const { hasRole } = usePermissions();
  const canEditTemplates = hasRole("SuperAdmin");
  const queryClient = useQueryClient();
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [keySearch, setKeySearch] = useState<string>("");
  const [page, setPage] = useState(1);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EmailTemplateListDto | null>(
    null,
  );
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplateListDto | null>(
    null,
  );

  const { data: response, isLoading } = useQuery({
    queryKey: ["admin", "email-templates", { categoryFilter, keySearch, page }],
    queryFn: () =>
      adminEmailTemplateService.getTemplates({
        category: categoryFilter === "All" ? undefined : categoryFilter,
        key: keySearch || undefined,
        page,
        limit: 20,
      }),
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      key: string;
      subject: string;
      body: string;
      category: string;
      culture: string;
    }) => adminEmailTemplateService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Email template created");
      setIsFormOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        subject?: string;
        body?: string;
        category?: string;
        culture?: string;
        isActive?: boolean;
      };
    }) => adminEmailTemplateService.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Email template updated");
      setIsFormOpen(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminEmailTemplateService.deleteTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "email-templates"] });
      toast.success("Email template deleted");
      setDeleteTarget(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const items = response?.data || [];
  const totalPages = response?.pagination?.totalPages || 1;
  const currentPage = response?.pagination?.currentPage || 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Email Templates"
        subtitle="Manage email notification templates"
        actions={
          !canEditTemplates
            ? []
            : [
                {
                  label: "Add Template",
                  onClick: () => {
                    setEditingItem(null);
                    setIsFormOpen(true);
                  },
                  icon: <Plus className="mr-2 h-4 w-4" />,
                },
              ]
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by key..."
                value={keySearch}
                onChange={(e) => {
                  setKeySearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={categoryFilter === "All" ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setCategoryFilter("All");
                  setPage(1);
                }}
              >
                All
              </Button>
              {EMAIL_TEMPLATE_CATEGORIES.map((cat) => (
                <Button
                  key={cat}
                  variant={categoryFilter === cat ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setCategoryFilter(cat);
                    setPage(1);
                  }}
                >
                  {CATEGORY_LABELS[cat] || cat}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Templates ({response?.pagination?.totalItems || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-muted-foreground">
              Loading...
            </div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No email templates found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Culture</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">
                      {item.key}
                    </TableCell>
                    <TableCell>{item.subject}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {CATEGORY_LABELS[item.category] || item.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{item.culture}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.isActive ? "default" : "outline"}>
                        {item.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <PermissionGate roles="SuperAdmin">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingItem(item);
                              setIsFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(item)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive-ink" />
                          </Button>
                        </PermissionGate>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {totalPages > 1 && (
            <div className="mt-4 flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setPage(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="py-2 text-sm text-muted-foreground">
                {currentPage} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <TemplateFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        item={editingItem}
        onCreate={(data) => createMutation.mutate(data)}
        onUpdate={(id, data) => updateMutation.mutate({ id, data })}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Email Template</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogBody>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTarget?.key}"? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogBody>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                deleteTarget && deleteMutation.mutate(deleteTarget.id)
              }
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: EmailTemplateListDto | null;
  onCreate: (data: {
    key: string;
    subject: string;
    body: string;
    category: string;
    culture: string;
  }) => void;
  onUpdate: (
    id: string,
    data: {
      subject?: string;
      body?: string;
      category?: string;
      culture?: string;
      isActive?: boolean;
    },
  ) => void;
  isSubmitting: boolean;
}

function TemplateFormDialog({
  open,
  onOpenChange,
  item,
  onCreate,
  onUpdate,
  isSubmitting,
}: TemplateFormDialogProps) {
  const isEdit = !!item;
  const [key, setKey] = useState(item?.key || "");
  const [subject, setSubject] = useState(item?.subject || "");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState(item?.category || "System");
  const [culture, setCulture] = useState(item?.culture || "en");
  const [isActive, setIsActive] = useState(item?.isActive ?? true);

  // Reset form state when item changes or dialog opens
  useEffect(() => {
    if (open) {
      setKey(item?.key || "");
      setSubject(item?.subject || "");
      setCategory(item?.category || "System");
      setCulture(item?.culture || "en");
      setIsActive(item?.isActive ?? true);
      if (!item) {
        setBody("");
      }
    }
  }, [item, open]);

  // Load body when editing
  const { data: detail } = useQuery({
    queryKey: ["admin", "email-templates", item?.id],
    queryFn: () => adminEmailTemplateService.getTemplateById(item!.id),
    enabled: isEdit && !!item?.id && open,
  });

  const bodyFromDetail = detail?.body;

  useEffect(() => {
    if (bodyFromDetail !== undefined) {
      setBody(bodyFromDetail);
    }
  }, [bodyFromDetail]);

  const handleSubmit = () => {
    if (isEdit && item) {
      onUpdate(item.id, {
        subject: subject || undefined,
        body: body || undefined,
        category: category || undefined,
        culture: culture || undefined,
        isActive,
      });
    } else {
      onCreate({
        key,
        subject,
        body,
        category,
        culture,
      });
    }
  };

  const isValid = isEdit
    ? subject && body && category && culture
    : key && subject && body && category && culture;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Email Template" : "Add Email Template"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <DialogDescription>
            {isEdit
              ? "Update this email notification template"
              : "Create a new email notification template"}
          </DialogDescription>
          {!isEdit && (
            <div className="space-y-2">
              <Label htmlFor="key">
                Key <span className="text-destructive-ink">*</span>
              </Label>
              <Input
                id="key"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="e.g., welcome_email"
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier for this template (use snake_case)
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-destructive-ink">*</span>
            </Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">
              Body <span className="text-destructive-ink">*</span>
            </Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Email body content (supports HTML)"
              rows={10}
              className="font-mono text-sm"
            />
            <p className="text-xs text-muted-foreground">
              Use {"{{variable}}"} syntax for placeholders. HTML is supported.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="category">
                Category <span className="text-destructive-ink">*</span>
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EMAIL_TEMPLATE_CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {CATEGORY_LABELS[cat] || cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="culture">
                Culture <span className="text-destructive-ink">*</span>
              </Label>
              <Select value={culture} onValueChange={setCulture}>
                <SelectTrigger id="culture">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="vi">Vietnamese</SelectItem>
                  <SelectItem value="ja">Japanese</SelectItem>
                  <SelectItem value="ko">Korean</SelectItem>
                  <SelectItem value="zh">Chinese</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isEdit && (
            <div className="flex items-center gap-2">
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={setIsActive}
              />
              <Label htmlFor="isActive">Active</Label>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !isValid}>
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
